
import {
  Eye,
  User,
  Calendar,
  ArrowRight,
  ArrowLeft,
  Tag,
} from "lucide-react";


const toYoutubeEmbed = (url = "") => {
  if (!url) return "";

  // Shorts
  const shorts = url.match(/youtube\.com\/shorts\/([^?&#/]+)/i);
  if (shorts?.[1]) return `https://www.youtube.com/embed/${shorts[1]}`;

  // youtu.be
  const short = url.match(/youtu\.be\/([^?&#/]+)/i);
  if (short?.[1]) return `https://www.youtube.com/embed/${short[1]}`;

  // watch?v=
  const watch = url.match(/[?&]v=([^?&#/]+)/i);
  if (watch?.[1]) return `https://www.youtube.com/embed/${watch[1]}`;

  // already embed
  if (/youtube\.com\/embed\//i.test(url)) return url;

  return url;
};

const SinglePage1 = ({ article, onBack, onPrev, onNext }) => {
  const isVideo = article?.embed_link || article?.video;
  const iframeUrl = toYoutubeEmbed(isVideo);

  return (
    <div className=" bg-white  shadow-xl rounded-md mt-1">
      <div className="px-4 py-2 flex flex-wrap items-center gap-3 sm:flex-nowrap justify-between">
        {/* LEFT: Back */}
        <button
          onClick={onBack}
          className="order-1 sm:order-none group inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900"
        >
          <ArrowLeft
            size={16}
            className="transition-transform group-hover:-translate-x-1"
          />
          Back to list
        </button>

        {/* CENTER: Prev / Next */}
        <div className="order-3 w-full sm:order-none sm:w-auto flex justify-center gap-3">
          {/* PREV */}
          <button
            onClick={onPrev}
            className="group flex items-center gap-2 px-4 py-2 rounded-xl
        border border-gray-200 bg-white
        hover:border-orange-400 hover:bg-orange-50
        disabled:opacity-40"
          >
            <ArrowLeft
              size={16}
              className="transition-transform group-hover:-translate-x-1"
            />
            <span className="text-sm font-semibold">Prev</span>
          </button>

          {/* NEXT */}
          <button
            onClick={onNext}
            className="group flex items-center gap-2 px-4 py-2 rounded-xl
        bg-orange-500 text-white
        hover:bg-orange-600
        disabled:opacity-40"
          >
            <span className="text-sm font-semibold">Next</span>
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-1"
            />
          </button>
        </div>

        {/* RIGHT: Visit */}
        {article?.post_url && (
          <a
            href={article.post_url}
            target="_blank"
            rel="noopener noreferrer"
            className="
        order-2 sm:order-none
        inline-flex items-center gap-1.5
        px-3 py-1.5 rounded-md
        bg-gray-100 text-gray-700
        font-medium text-xs sm:text-sm
        hover:bg-gray-200 transition-colors
      "
          >
            Visit on Site <span>↗</span>
          </a>
        )}
      </div>

      {/* HERO IMAGE */}
      <div className="w-full overflow-hidden  bg-gray-100 ">
        {isVideo ? (
          // Video - fixed height
          <iframe
            src={iframeUrl}
            title={article?.title}
            className="w-full h-[380px] sm:h-[480px] lg:h-[600px]"
            frameBorder="0"
            allowFullScreen
          />
        ) : (
          // Image - full, no crop
          <img
            src={article?.cover_image || article?.image}
            alt={article?.title}
            className="w-full h-auto max-h-[80vh] object-fill"
          />
        )}
      </div>

      {/* CONTENT */}
      <div className={` mx-auto relative z-30 select-none`}>
        <article className=" rounded-2xl ">
          <div className=" px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center gap-3 sm:gap-4 mb-5">
              {/* RIGHT CONTENT */}
              <div className="flex-1 min-w-0">
                {/* TITLE */}
                <h1
                  className="
          text-[18px] sm:text-2xl lg:text-3xl
          font-extrabold tracking-tight
          text-gray-900
          leading-snug
          line-clamp-2
        "
                  title={article?.title || article?.name}
                >
                  {article?.title || article?.name}
                </h1>

                {/* META */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {/* AUTHOR */}
                  <span className="flex items-center gap-1 md:px-2.5 py-1">
                    <Tag size={15} className="text-orange-500 flex-shrink-0" />
                    <span className="font-semibold text-orange-600 text-[12px]">
                      {article?.itemcategory_name || "Category"}
                    </span>
                  </span>
                  <span
                    className="
            inline-flex items-center gap-1
            rounded-full md:px-2.5 py-1
            text-[13px] font-semibold
          text-gray-800
        
          "
                  >
                    <User size={14} className="opacity-70" />
                    <span className="truncate max-w-[160px] sm:max-w-[220px]">
                      {article?.author || "THT"}
                    </span>
                  </span>
                  {/* DATE */}
                  <span
                    className="
            inline-flex items-center gap-1
            rounded-full md:px-2.5 py-1
            text-[12px]  font-medium
            text-gray-700
          
          "
                  >
                    <Calendar size={14} className="opacity-70" />
                    {article?.created_at
                      ? new Date(article.created_at).toLocaleDateString(
                          "en-IN",
                          {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          },
                        )
                      : "—"}
                  </span>

                  {/* VIEWS */}
                  <span
                    className="
            inline-flex items-center gap-1
            rounded-full  md:px-2.5 py-1
            text-[12px] font-semibold
            text-orange-700
          "
                  >
                    <Eye size={14} className="opacity-90" />
                    {article?.view ?? 0}
                    <span className="font-medium opacity-80 text-orange-600 ">
                      views
                    </span>
                  </span>
                </div>
              </div>
            </div>
            <div
              className="description "
              dangerouslySetInnerHTML={{
                __html: article?.cover_description || article?.description,
              }}
            />
          </div>

          <div className="px-4 sm:px-6 lg:px-8  py-2 flex bg-white flex-wrap items-center gap-3 sm:flex-nowrap justify-between">
            {/* LEFT: Back */}
            <button
              onClick={onBack}
              className="order-1 sm:order-none group inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900"
            >
              <ArrowLeft
                size={16}
                className="transition-transform group-hover:-translate-x-1"
              />
              Back to list
            </button>

            {/* CENTER: Prev / Next */}
            <div className="order-3 w-full sm:order-none sm:w-auto flex justify-center gap-3">
              {/* PREV */}
              <button
                onClick={onPrev}
                className="group flex items-center gap-2 px-4 py-2 rounded-xl
          border border-gray-200 bg-white
          hover:border-orange-400 hover:bg-orange-50
          disabled:opacity-40"
              >
                <ArrowLeft
                  size={16}
                  className="transition-transform group-hover:-translate-x-1"
                />
                <span className="text-sm font-semibold">Prev</span>
              </button>

              {/* NEXT */}
              <button
                onClick={onNext}
                className="group flex items-center gap-2 px-4 py-2 rounded-xl
          bg-orange-500 text-white
          hover:bg-orange-600
          disabled:opacity-40"
              >
                <span className="text-sm font-semibold">Next</span>
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
            </div>

            {/* RIGHT: Visit */}
            {article?.post_url && (
              <a
                href={article.post_url}
                target="_blank"
                rel="noopener noreferrer"
                className="
          order-2 sm:order-none
          inline-flex items-center gap-1.5
          px-3 py-1.5 rounded-md
          bg-gray-100 text-gray-700
          font-medium text-xs sm:text-sm
          hover:bg-gray-200 transition-colors
        "
              >
                Visit on Site <span>↗</span>
              </a>
            )}
          </div>
        </article>
      </div>
    </div>
  );
};



export default SinglePage1;
